import type { ButtonHTMLAttributes } from 'react';
import { cn } from './cn.ts';

type Variant = 'primary' | 'secondary' | 'ghost' | 'onDark' | 'danger';
type Size = 'sm' | 'md';

/**
 * The focus rings are **slate-500, not slate-400** (P4-16c).
 *
 * WCAG 1.4.11 asks a focus indicator for 3:1 against what is adjacent to
 * it, and the ring is drawn with `outline-offset-2`, so what is adjacent
 * is the page behind the button. slate-400 managed **2.56:1 on white** and
 * 2.34:1 on a striped row — a real AA failure on the two most-used button
 * variants in the product. slate-500 is 4.76:1 and 4.34:1.
 *
 * Nothing could have caught this by looking: axe measures what is painted
 * and a focus ring is not text, so the automated sweep was never auditing
 * it. `packages/tokens/src/contrast.test.ts` computes the ratios from the
 * tokens instead, which is why this is now a test rather than a note.
 */
const VARIANT: Record<Variant, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:outline-blue-600',
  secondary:
    'bg-white text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus-visible:outline-slate-500',
  ghost: 'text-slate-600 hover:bg-slate-100 focus-visible:outline-slate-500',
  /**
   * A quiet button on a dark ground — the app header.
   *
   * `ghost` was used there and failed contrast badly: slate-600 on
   * slate-900 is 2.35:1 against a required 4.5. A variant rather than a
   * className at the call site, because the next person putting a button in
   * the header would make the same mistake.
   */
  onDark: 'text-slate-200 hover:bg-slate-800 hover:text-white focus-visible:outline-slate-300',
  danger: 'bg-red-600 text-white hover:bg-red-700 focus-visible:outline-red-600',
};

const SIZE: Record<Size, string> = {
  sm: 'px-2.5 py-1.5 text-sm',
  md: 'px-3.5 py-2 text-sm',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: Variant;
  readonly size?: Size;
  readonly loading?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  className,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANT[variant],
        SIZE[size],
        className,
      )}
    >
      {loading && (
        <span
          aria-hidden="true"
          className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
}
